import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { useMemo, useState } from "react";
import { RefreshCw, TrendingUp, TrendingDown, Minus, Search, Trash2, ArrowUp, ArrowDown, ArrowUpDown, Plus, Pencil, Check, X } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";

type RangeKey = "7d" | "30d" | "90d" | "1y" | "all";
const RANGE_DAYS: Record<RangeKey, number | null> = { "7d": 7, "30d": 30, "90d": 90, "1y": 365, all: null };

interface PricePoint {
  id: string;
  scraped_at: string;
  lowest_price: number;
  average_price: number | null;
  source: string;
  offer_count: number | null;
}

interface ModelRow {
  id: string;
  brand: string | null;
  model_name: string;
  geizhals_url: string | null;
  geizhals_id: string | null;
  category: string;
  latest_lowest: number | null;
  latest_average: number | null;
  latest_source: string | null;
  prev_lowest: number | null;
  history: PricePoint[];
  sources: string[];
  isManual: boolean;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function AppliancePriceTracker() {
  const { t } = useTranslation("admin");
  const { allowed: isAdmin, isLoading: roleLoading } = useCanManageCompany();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [brandFilter, setBrandFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [range, setRange] = useState<RangeKey>("30d");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ModelRow | null>(null);
  const [deletePriceTarget, setDeletePriceTarget] = useState<PricePoint | null>(null);
  const [sortKey, setSortKey] = useState<"model" | "lowest" | "change">("model");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Add manual model dialog
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [newModel, setNewModel] = useState({ brand: "", model_name: "", category: "washing_machine", image_url: "" });

  // New price-point form (per detail sheet)
  const [newPrice, setNewPrice] = useState<{ date: string; lowest: string; average: string; offers: string; source: string }>({
    date: todayIso(), lowest: "", average: "", offers: "", source: "manual",
  });

  // Inline edit state for an existing price row
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState<{ date: string; lowest: string; average: string; offers: string; source: string }>({
    date: "", lowest: "", average: "", offers: "", source: "",
  });

  const toggleSort = (key: "model" | "lowest" | "change") => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "change" ? "asc" : key === "lowest" ? "asc" : "asc");
    }
  };

  const { data: lastRun } = useQuery({
    queryKey: ["scrape-runs-latest"],
    queryFn: async () => {
      const { data } = await supabase
        .from("scrape_runs" as any)
        .select("*")
        .eq("source", "geizhals")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data as any;
    },
    refetchInterval: 5000,
  });

  const { data: brandList = [] } = useQuery({
    queryKey: ["brands-list"],
    queryFn: async () => {
      const { data, error } = await supabase.from("brands").select("name").order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((b: any) => b.name as string);
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["appliance-prices", range],
    queryFn: async () => {
      const days = RANGE_DAYS[range];
      const sinceIso = days ? new Date(Date.now() - days * 86400_000).toISOString() : null;

      const PAGE = 1000;
      const fetchAll = async <T,>(builder: (from: number, to: number) => any): Promise<T[]> => {
        const all: T[] = [];
        for (let from = 0; ; from += PAGE) {
          const to = from + PAGE - 1;
          const { data, error } = await builder(from, to);
          if (error) throw error;
          const chunk = (data ?? []) as T[];
          all.push(...chunk);
          if (chunk.length < PAGE) break;
        }
        return all;
      };

      const models = await fetchAll<any>((from, to) =>
        supabase
          .from("new_appliance_models" as any)
          .select("id, brand, model_name, geizhals_url, geizhals_id, image_url, category")
          .order("brand", { ascending: true })
          .range(from, to),
      );

      const prices = await fetchAll<any>((from, to) => {
        let q = supabase
          .from("new_appliance_prices" as any)
          .select("id, model_id, lowest_price, average_price, scraped_at, source, offer_count")
          .order("scraped_at", { ascending: true })
          .range(from, to);
        if (sinceIso) q = q.gte("scraped_at", sinceIso);
        return q;
      });

      const byModel = new Map<string, PricePoint[]>();
      for (const p of (prices ?? []) as any[]) {
        const arr = byModel.get(p.model_id) ?? [];
        arr.push({
          id: p.id,
          scraped_at: p.scraped_at,
          lowest_price: Number(p.lowest_price),
          average_price: p.average_price != null ? Number(p.average_price) : null,
          source: p.source ?? "geizhals.de",
          offer_count: p.offer_count ?? null,
        });
        byModel.set(p.model_id, arr);
      }

      const rows: ModelRow[] = (models ?? []).map((m: any) => {
        const hist = byModel.get(m.id) ?? [];
        const latest = hist[hist.length - 1] ?? null;
        const prev = hist.length > 1 ? hist[0] : null;
        return {
          id: m.id,
          brand: m.brand,
          model_name: m.model_name,
          geizhals_url: m.geizhals_url,
          geizhals_id: m.geizhals_id,
          category: m.category,
          latest_lowest: latest?.lowest_price ?? null,
          latest_average: latest?.average_price ?? null,
          latest_source: latest?.source ?? null,
          prev_lowest: prev?.lowest_price ?? null,
          history: hist,
          sources: Array.from(new Set(hist.map((h) => h.source))),
          isManual: !m.geizhals_id || String(m.geizhals_id).startsWith("manual-"),
        };
      });

      return rows;
    },
  });

  const brands = useMemo(() => {
    const s = new Set<string>();
    (data ?? []).forEach((r) => r.brand && s.add(r.brand));
    return Array.from(s).sort();
  }, [data]);

  const sources = useMemo(() => {
    const s = new Set<string>();
    (data ?? []).forEach((r) => r.sources.forEach((src) => s.add(src)));
    return Array.from(s).sort();
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = (data ?? [])
      .filter((r) => brandFilter === "all" || r.brand === brandFilter)
      .filter((r) => sourceFilter === "all" || r.sources.includes(sourceFilter))
      .filter((r) => !q || r.model_name.toLowerCase().includes(q))
      .filter((r) => r.latest_lowest != null || r.isManual);

    const changeOf = (r: ModelRow) =>
      r.prev_lowest && r.latest_lowest ? ((r.latest_lowest - r.prev_lowest) / r.prev_lowest) * 100 : null;

    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...base].sort((a, b) => {
      if (sortKey === "model") return a.model_name.localeCompare(b.model_name) * dir;
      if (sortKey === "lowest") return ((a.latest_lowest ?? 0) - (b.latest_lowest ?? 0)) * dir;
      const ca = changeOf(a);
      const cb = changeOf(b);
      if (ca == null && cb == null) return 0;
      if (ca == null) return 1;
      if (cb == null) return -1;
      return (ca - cb) * dir;
    });
    return sorted;
  }, [data, brandFilter, sourceFilter, search, sortKey, sortDir]);

  const selected = useMemo(() => (data ?? []).find((r) => r.id === selectedId) ?? null, [data, selectedId]);

  const runScrape = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("scrape-appliance-prices-orchestrator", { body: {} });
      if (error) throw error;
      return data;
    },
    onSuccess: (res: any) => {
      toast({
        title: res?.done ? "Scrape complete" : "Scrape batch finished",
        description: res?.done
          ? `Last page: ${res?.lastPage}`
          : `Scraping continues in the background from page ${res?.nextStartPage}. Refresh in ~1 min.`,
      });
      qc.invalidateQueries({ queryKey: ["appliance-prices"] });
      qc.invalidateQueries({ queryKey: ["scrape-runs-latest"] });
    },
    onError: (err: any) => {
      toast({ title: "Scrape failed", description: err?.message ?? "", variant: "destructive" });
    },
  });

  const deleteModel = useMutation({
    mutationFn: async (id: string) => {
      // Manually clear price history first (no FK cascade guaranteed)
      await supabase.from("new_appliance_prices" as any).delete().eq("model_id", id);
      const { data, error } = await supabase.from("new_appliance_models" as any).delete().eq("id", id).select("id");
      if (error) throw error;
      if (!data || (data as unknown[]).length === 0) throw new Error("You don't have permission to delete this model.");
    },
    onSuccess: () => {
      toast({ title: "Model removed", description: "Model and its price history were deleted." });
      if (selectedId === deleteTarget?.id) setSelectedId(null);
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ["appliance-prices"] });
    },
    onError: (err: any) => {
      toast({ title: "Delete failed", description: err?.message ?? "", variant: "destructive" });
    },
  });

  const addModel = useMutation({
    mutationFn: async () => {
      if (!newModel.brand.trim() || !newModel.model_name.trim()) {
        throw new Error("Brand and model name are required");
      }
      const payload = {
        brand: newModel.brand.trim(),
        model_name: newModel.model_name.trim(),
        category: newModel.category,
        image_url: newModel.image_url.trim() || null,
        geizhals_id: `manual-${crypto.randomUUID()}`,
        geizhals_url: null,
      };
      const { data, error } = await supabase.from("new_appliance_models" as any).insert(payload).select().single();
      if (error) throw error;
      return data as any;
    },
    onSuccess: (row: any) => {
      toast({ title: "Model added", description: `${row.brand} ${row.model_name}` });
      setAddModelOpen(false);
      setNewModel({ brand: "", model_name: "", category: "washing_machine", image_url: "" });
      qc.invalidateQueries({ queryKey: ["appliance-prices"] });
      setSelectedId(row.id);
    },
    onError: (err: any) => {
      toast({ title: "Could not add model", description: err?.message ?? "", variant: "destructive" });
    },
  });

  const addPrice = useMutation({
    mutationFn: async (modelId: string) => {
      const lowest = parseFloat(newPrice.lowest);
      if (!isFinite(lowest) || lowest <= 0) throw new Error("Lowest price must be a positive number");
      const avg = newPrice.average.trim() ? parseFloat(newPrice.average) : null;
      const offers = newPrice.offers.trim() ? parseInt(newPrice.offers, 10) : null;
      // Store as midnight UTC of the chosen date
      const scrapedAt = new Date(`${newPrice.date}T12:00:00Z`).toISOString();
      const { error } = await supabase.from("new_appliance_prices" as any).insert({
        model_id: modelId,
        lowest_price: lowest,
        average_price: avg,
        offer_count: offers,
        scraped_at: scrapedAt,
        source: newPrice.source.trim() || "manual",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Price added" });
      setNewPrice({ date: todayIso(), lowest: "", average: "", offers: "", source: "manual" });
      qc.invalidateQueries({ queryKey: ["appliance-prices"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not add price", description: err?.message ?? "", variant: "destructive" });
    },
  });

  const updatePrice = useMutation({
    mutationFn: async (id: string) => {
      const lowest = parseFloat(editPrice.lowest);
      if (!isFinite(lowest) || lowest <= 0) throw new Error("Lowest price must be a positive number");
      const avg = editPrice.average.trim() ? parseFloat(editPrice.average) : null;
      const offers = editPrice.offers.trim() ? parseInt(editPrice.offers, 10) : null;
      const scrapedAt = new Date(`${editPrice.date}T12:00:00Z`).toISOString();
      const { error } = await supabase.from("new_appliance_prices" as any).update({
        lowest_price: lowest,
        average_price: avg,
        offer_count: offers,
        scraped_at: scrapedAt,
        source: editPrice.source.trim() || "manual",
      }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Price updated" });
      setEditingPriceId(null);
      qc.invalidateQueries({ queryKey: ["appliance-prices"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not update price", description: err?.message ?? "", variant: "destructive" });
    },
  });

  const deletePrice = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("new_appliance_prices" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Price deleted" });
      setDeletePriceTarget(null);
      qc.invalidateQueries({ queryKey: ["appliance-prices"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not delete price", description: err?.message ?? "", variant: "destructive" });
    },
  });

  const startEditPrice = (p: PricePoint) => {
    setEditingPriceId(p.id);
    setEditPrice({
      date: p.scraped_at.slice(0, 10),
      lowest: String(p.lowest_price),
      average: p.average_price != null ? String(p.average_price) : "",
      offers: p.offer_count != null ? String(p.offer_count) : "",
      source: p.source,
    });
  };

  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  // Access enforced by the route guard in App.tsx.

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <SettingsBackLink to="/platform/data" label={t("nav.data", { ns: "settings" })} />
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">{t("prices.appliance.title")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setAddModelOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" /> Add manual model
            </Button>
            <Button onClick={() => runScrape.mutate()} disabled={runScrape.isPending} className="gap-2">
              <RefreshCw className={`w-4 h-4 ${runScrape.isPending ? "animate-spin" : ""}`} />
              Run scrape now
            </Button>
          </div>
        </div>
        <p className="text-muted-foreground">
          Daily Geizhals washing-machine prices + admin-managed legacy entries ·{" "}
          {lastRun?.started_at
            ? <>Last run {lastRun.status} at {new Date(lastRun.started_at).toLocaleString()}{lastRun.models_seen != null && <> · {lastRun.models_seen} models, {lastRun.prices_inserted} snapshots</>}</>
            : "No scrape yet"}
        </p>
      </div>

      <div className="px-6 space-y-4">
        <div className="bg-card rounded-xl border border-border p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search model…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <SearchableSelect
            className="w-[180px]"
            value={brandFilter}
            onChange={(v) => setBrandFilter(v ?? "all")}
            placeholder="Brand"
            searchPlaceholder="Search brands…"
            emptyMessage="No brands found"
            items={[{ value: "all", label: "All brands" }, ...brands.map((b) => ({ value: b, label: b }))]}
          />
          <SearchableSelect
            className="w-[160px]"
            value={sourceFilter}
            onChange={(v) => setSourceFilter(v ?? "all")}
            placeholder="Source"
            searchPlaceholder="Search sources…"
            emptyMessage="No sources found"
            items={[{ value: "all", label: "All sources" }, ...sources.map((s) => ({ value: s, label: s }))]}
          />
          <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
            <SelectTrigger className="w-[160px]"><SelectValue placeholder="Range" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground ml-auto">{filtered.length} models</div>
        </div>

        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="grid grid-cols-[1fr_120px_120px_120px_120px_56px] gap-3 px-4 py-3 text-xs font-medium text-muted-foreground bg-muted/40 border-b border-border">
            <button onClick={() => toggleSort("model")} className="flex items-center gap-1 text-left hover:text-foreground transition-colors">
              Model {sortKey === "model" ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
            </button>
            <button onClick={() => toggleSort("lowest")} className="flex items-center justify-end gap-1 hover:text-foreground transition-colors">
              Lowest {sortKey === "lowest" ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
            </button>
            <div className="text-right">Avg</div>
            <button onClick={() => toggleSort("change")} className="flex items-center justify-end gap-1 hover:text-foreground transition-colors">
              Change {sortKey === "change" ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
            </button>
            <div className="text-right">Snapshots</div>
            <div className="text-right">Actions</div>
          </div>
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No data yet. Click <span className="font-medium text-foreground">Run scrape now</span> or <span className="font-medium text-foreground">Add manual model</span>.
            </div>
          ) : (
            <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
              {filtered.map((r) => {
                const change = r.prev_lowest && r.latest_lowest ? ((r.latest_lowest - r.prev_lowest) / r.prev_lowest) * 100 : null;
                return (
                  <div key={r.id} className="grid grid-cols-[1fr_120px_120px_120px_120px_56px] gap-3 px-4 py-3 items-center hover:bg-muted/40 transition-colors">
                    <button onClick={() => setSelectedId(r.id)} className="text-left min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{r.model_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {r.brand && <span className="text-xs text-muted-foreground">{r.brand}</span>}
                        {r.isManual && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">Manual</Badge>}
                        {r.latest_source && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {r.latest_source}
                          </span>
                        )}
                      </div>
                    </button>
                    <button onClick={() => setSelectedId(r.id)} className="text-right text-sm font-semibold text-foreground">
                      {r.latest_lowest != null ? `€ ${r.latest_lowest.toFixed(0)}` : "—"}
                    </button>
                    <button onClick={() => setSelectedId(r.id)} className="text-right text-sm text-muted-foreground">{r.latest_average ? `€ ${r.latest_average.toFixed(0)}` : "—"}</button>
                    <button onClick={() => setSelectedId(r.id)} className="text-right text-sm">
                      {change == null ? (
                        <span className="text-muted-foreground inline-flex items-center gap-1"><Minus className="w-3 h-3" />—</span>
                      ) : change > 0 ? (
                        <span className="text-destructive inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" />{change.toFixed(1)}%</span>
                      ) : change < 0 ? (
                        <span className="text-primary inline-flex items-center gap-1"><TrendingDown className="w-3 h-3" />{change.toFixed(1)}%</span>
                      ) : (
                        <span className="text-muted-foreground inline-flex items-center gap-1"><Minus className="w-3 h-3" />0%</span>
                      )}
                    </button>
                    <button onClick={() => setSelectedId(r.id)} className="text-right text-sm text-muted-foreground">{r.history.length}</button>
                    <div className="flex justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }} aria-label={`Remove ${r.model_name}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-left flex items-center gap-2">
                  {selected.model_name}
                  {selected.isManual && <Badge variant="secondary">Manual</Badge>}
                </SheetTitle>
                <p className="text-sm text-muted-foreground text-left">{selected.brand}</p>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-muted/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Latest lowest</p>
                    <p className="text-lg font-semibold text-foreground">{selected.latest_lowest != null ? `€ ${selected.latest_lowest.toFixed(0)}` : "—"}</p>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Latest avg</p>
                    <p className="text-lg font-semibold text-foreground">{selected.latest_average ? `€ ${selected.latest_average.toFixed(0)}` : "—"}</p>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Snapshots</p>
                    <p className="text-lg font-semibold text-foreground">{selected.history.length}</p>
                  </div>
                </div>

                {selected.history.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-3">
                    <p className="text-sm font-medium text-foreground mb-2">Price history ({range})</p>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={selected.history.map((h) => ({ ...h, date: new Date(h.scraped_at).toLocaleDateString() }))}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                          <RechartTooltip />
                          <Line type="monotone" dataKey="lowest_price" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} name="Lowest" />
                          <Line type="monotone" dataKey="average_price" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} dot={false} name="Average" strokeDasharray="4 4" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Editable price history */}
                <div className="bg-card border border-border rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground">Manage price snapshots</p>
                  </div>

                  {/* Add new */}
                  <div className="grid grid-cols-[120px_90px_90px_70px_110px_auto] gap-2 items-end">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Date</Label>
                      <Input type="date" value={newPrice.date} onChange={(e) => setNewPrice({ ...newPrice, date: e.target.value })} className="h-8" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Lowest €</Label>
                      <Input type="number" step="0.01" value={newPrice.lowest} onChange={(e) => setNewPrice({ ...newPrice, lowest: e.target.value })} className="h-8" placeholder="0.00" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Avg €</Label>
                      <Input type="number" step="0.01" value={newPrice.average} onChange={(e) => setNewPrice({ ...newPrice, average: e.target.value })} className="h-8" placeholder="—" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Offers</Label>
                      <Input type="number" step="1" value={newPrice.offers} onChange={(e) => setNewPrice({ ...newPrice, offers: e.target.value })} className="h-8" placeholder="—" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Source</Label>
                      <Input value={newPrice.source} onChange={(e) => setNewPrice({ ...newPrice, source: e.target.value })} className="h-8" />
                    </div>
                    <Button size="sm" className="gap-1" disabled={addPrice.isPending} onClick={() => addPrice.mutate(selected.id)}>
                      <Plus className="w-3.5 h-3.5" /> Add
                    </Button>
                  </div>

                  {/* List */}
                  <div className="border-t border-border">
                    {selected.history.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-3 text-center">No price snapshots yet for this range.</p>
                    ) : (
                      <div className="divide-y divide-border max-h-72 overflow-y-auto">
                        {[...selected.history].reverse().map((p) => (
                          <div key={p.id} className="py-2">
                            {editingPriceId === p.id ? (
                              <div className="grid grid-cols-[120px_90px_90px_70px_110px_auto] gap-2 items-end">
                                <Input type="date" value={editPrice.date} onChange={(e) => setEditPrice({ ...editPrice, date: e.target.value })} className="h-8" />
                                <Input type="number" step="0.01" value={editPrice.lowest} onChange={(e) => setEditPrice({ ...editPrice, lowest: e.target.value })} className="h-8" />
                                <Input type="number" step="0.01" value={editPrice.average} onChange={(e) => setEditPrice({ ...editPrice, average: e.target.value })} className="h-8" />
                                <Input type="number" step="1" value={editPrice.offers} onChange={(e) => setEditPrice({ ...editPrice, offers: e.target.value })} className="h-8" />
                                <Input value={editPrice.source} onChange={(e) => setEditPrice({ ...editPrice, source: e.target.value })} className="h-8" />
                                <div className="flex gap-1">
                                  <Button size="icon" className="h-8 w-8" disabled={updatePrice.isPending} onClick={() => updatePrice.mutate(p.id)}>
                                    <Check className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingPriceId(null)}>
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="grid grid-cols-[120px_90px_90px_70px_110px_auto] gap-2 items-center text-sm">
                                <span className="text-muted-foreground">{new Date(p.scraped_at).toLocaleDateString()}</span>
                                <span className="font-medium text-foreground">€ {p.lowest_price.toFixed(0)}</span>
                                <span className="text-muted-foreground">{p.average_price != null ? `€ ${p.average_price.toFixed(0)}` : "—"}</span>
                                <span className="text-muted-foreground">{p.offer_count ?? "—"}</span>
                                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground inline-block w-fit">{p.source}</span>
                                <div className="flex gap-1 justify-end">
                                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEditPrice(p)} aria-label="Edit price">
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeletePriceTarget(p)} aria-label="Delete price">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {selected.isManual ? (
                  <p className="text-xs text-muted-foreground">This model is admin-managed. Prices here are entered manually and not affected by scraping.</p>
                ) : selected.geizhals_url ? (
                  <a href={selected.geizhals_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
                    Open on Geizhals →
                  </a>
                ) : null}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add manual model dialog */}
      <Dialog open={addModelOpen} onOpenChange={setAddModelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add manual model</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Brand</Label>
              <SearchableSelect
                value={newModel.brand || null}
                onChange={(v) => setNewModel({ ...newModel, brand: v ?? "" })}
                placeholder="Select brand"
                searchPlaceholder="Search brands…"
                emptyMessage="No brands found"
                items={brandList.map((b) => ({ value: b, label: b }))}
              />
            </div>
            <div>
              <Label>Model name</Label>
              <Input value={newModel.model_name} onChange={(e) => setNewModel({ ...newModel, model_name: e.target.value })} placeholder="e.g. W1 WCG670 WCS" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newModel.category} onValueChange={(v) => setNewModel({ ...newModel, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="washing_machine">Washing machine</SelectItem>
                  <SelectItem value="dryer">Dryer</SelectItem>
                  <SelectItem value="dishwasher">Dishwasher</SelectItem>
                  <SelectItem value="fridge">Fridge</SelectItem>
                  <SelectItem value="oven">Oven</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Image URL (optional)</Label>
              <Input value={newModel.image_url} onChange={(e) => setNewModel({ ...newModel, image_url: e.target.value })} placeholder="https://…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddModelOpen(false)}>Cancel</Button>
            <Button onClick={() => addModel.mutate()} disabled={addModel.isPending}>
              {addModel.isPending ? "Adding…" : "Add model"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove model?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-medium text-foreground">{deleteTarget?.model_name}</span>
              {" "}and all of its price history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteModel.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); if (deleteTarget) deleteModel.mutate(deleteTarget.id); }}
              disabled={deleteModel.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleteModel.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deletePriceTarget} onOpenChange={(o) => !o && setDeletePriceTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete price snapshot?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletePriceTarget && (
                <>This will permanently delete the price entry of € {deletePriceTarget.lowest_price.toFixed(0)} on {new Date(deletePriceTarget.scraped_at).toLocaleDateString()}.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePrice.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); if (deletePriceTarget) deletePrice.mutate(deletePriceTarget.id); }}
              disabled={deletePrice.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deletePrice.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
